import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Search, TrendingUp, TrendingDown, RefreshCw, AlertTriangle, Star, X, Bot, ChartLine } from 'lucide-react';

// Mapa de estrategias recomendadas por régimen según Atenea (mismo que playbook.py)
const PLAYBOOK_BOTS = {
  ALZA:  { regimen: 'tendencia_alcista_fuerte', estrategias: ['adx_trend','ema_cross','sma_trend','ichimoku'], evitar: ['bollinger_reversion','mean_reversion_z'] },
  BAJA:  { regimen: 'tendencia_bajista_fuerte', estrategias: ['adx_trend','dca_bot','macd_cross'], evitar: ['scalping','grid_trading'] },
};
const NOMBRES_ESTRATEGIAS = {
  adx_trend: 'ADX Trend Filter', ema_cross: 'EMA Cross 9/21', sma_trend: 'Trend Following SMA',
  ichimoku: 'Ichimoku TK Cross', dca_bot: 'DCA Bot', macd_cross: 'MACD Cross',
  bollinger_reversion: 'Bollinger Reversión', mean_reversion_z: 'Mean Reversion Z',
  scalping: 'Scalping', grid_trading: 'Grid Trading',
};

import { API } from '../config';
const o = { withCredentials: true };

const ScoreBadge = ({ score }) => {
  const color = score >= 90 ? '#FFD700' : score >= 80 ? '#00FF88' : '#00D4FF';
  const bg = score >= 90 ? 'rgba(255,215,0,0.1)' : score >= 80 ? 'rgba(0,255,136,0.1)' : 'rgba(0,212,255,0.1)';
  return (
    <div style={{ background: bg, border: `1px solid ${color}30`, color }}
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold min-w-[54px] justify-center">
      {score >= 90 && <Star className="w-3 h-3" />}
      {score}
    </div>
  );
};

const TfBadge = ({ dir, adx, label }) => {
  const color = dir === 'ALZA' ? '#00FF88' : dir === 'BAJA' ? '#FF3B30' : '#52525B';
  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] text-[#52525B]">{label}</span>
      <span style={{ color }} className="text-[11px] font-bold">{dir}</span>
      {adx && <span className="text-[10px] text-[#52525B]">{adx}</span>}
    </div>
  );
};

const Scanner = () => {
  const [exchanges, setExchanges] = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [oportunidades, setOportunidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [progreso, setProgreso] = useState({ analizados: 0, total: 0, encontrados: 0 });
  const [terminado, setTerminado] = useState(false);

  useEffect(() => {
    axios.get(`${API}/exchanges/keys`, o).then(r => {
      const exs = r.data.keys || [];
      setExchanges(exs);
      if (exs.length > 0) setSelectedExchange(exs[0].exchange_id);
    }).catch(() => {});
  }, []);

  const escanear = async () => {
    if (!selectedExchange) { toast.error('Selecciona un exchange'); return; }
    setLoading(true);
    setOportunidades([]);
    setTerminado(false);
    setProgreso({ analizados: 0, total: 0, encontrados: 0 });

    try {
      const response = await fetch(
        `${API}/scanner/oportunidades?exchange_id=${selectedExchange}`,
        { credentials: 'include' }
      );
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const msg = JSON.parse(line.slice(6));
            if (msg.tipo === 'inicio') {
              setProgreso(p => ({ ...p, total: msg.total }));
            } else if (msg.tipo === 'par') {
              setOportunidades(prev => {
                const next = [...prev, msg.data].sort((a, b) => b.score - a.score);
                return next;
              });
              setProgreso({ analizados: msg.analizados, total: msg.total, encontrados: msg.analizados });
            } else if (msg.tipo === 'progreso') {
              setProgreso({ analizados: msg.analizados, total: msg.total, encontrados: msg.encontrados });
            } else if (msg.tipo === 'fin') {
              setTerminado(true);
              setLastScan(new Date().toLocaleTimeString());
              setLoading(false);
              if (msg.encontrados === 0) toast.info('No se encontraron tendencias con score ≥ 70');
              else toast.success(`Escaneo completo: ${msg.encontrados} pares con tendencia fuerte`);
            } else if (msg.tipo === 'error') {
              toast.error(msg.mensaje || 'Error escaneando');
              setLoading(false);
            }
          } catch {}
        }
      }
    } catch (e) {
      toast.error('Error de conexión');
    }
    setLoading(false);
  };

  const [modal, setModal] = useState(null); // { symbol, direccion, score }

  const abrirModal = (op) => setModal(op);
  const cerrarModal = () => setModal(null);

  const irManual = (symbol) => {
    window.location.href = `/trading?symbol=${encodeURIComponent(symbol)}&exchange=${encodeURIComponent(selectedExchange)}`;
  };

  const irBots = (symbol) => {
    window.location.href = `/bots?symbol=${encodeURIComponent(symbol)}&exchange=${encodeURIComponent(selectedExchange)}`;
  };

  return (
    <Layout>
      <div className="p-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Search className="w-6 h-6 text-[#00D4FF]" />
          <h1 className="text-2xl font-bold text-white">Scanner de Tendencias</h1>
          <span className="text-xs text-[#52525B] bg-[#18181B] px-2 py-0.5 rounded-full border border-[#27272A]">Análisis multi-timeframe</span>
        </div>
        <p className="text-[#A1A1AA] text-sm mb-4">
          Atenea escanea todos los pares USDT del exchange. Analiza 1h + 4h + 1d con ADX, MACD, volumen y soporte/resistencia.
          Solo muestra pares con <strong className="text-white">score ≥ 70</strong> — tendencia real confirmada.
        </p>

        {/* Controles */}
        <Card className="bg-[#18181B] border-[#27272A] mb-5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <select value={selectedExchange} onChange={e => setSelectedExchange(e.target.value)}
                className="bg-[#09090B] border border-[#27272A] text-white rounded-lg px-3 py-2 text-sm">
                {exchanges.map(ex => (
                  <option key={ex.exchange_id} value={ex.exchange_id}>{ex.exchange_id.toUpperCase()}</option>
                ))}
              </select>
              <button onClick={escanear} disabled={loading}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#00D4FF] text-black font-bold text-sm hover:bg-[#00D4FF]/80 disabled:opacity-50 transition-all">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Escaneando...' : 'Escanear'}
              </button>
              {lastScan && (
                <span className="text-[#52525B] text-xs">
                  Último escaneo: {lastScan} · {oportunidades.length} oportunidades
                </span>
              )}
            </div>
            {loading && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-[#52525B] mb-1">
                  <span>Analizando {progreso.analizados} de {progreso.total} pares (1h+4h+1d)...</span>
                  <span className="text-[#00FF88]">{oportunidades.length} encontrados</span>
                </div>
                <div className="w-full bg-[#27272A] rounded-full h-1.5">
                  <div className="bg-[#00D4FF] h-1.5 rounded-full transition-all"
                    style={{ width: progreso.total ? `${(progreso.analizados/progreso.total)*100}%` : '0%' }} />
                </div>
              </div>
            )}
            {terminado && !loading && (
              <p className="text-xs text-[#00FF88] mt-2">
                ✓ Escaneo completo — {oportunidades.length} pares con tendencia fuerte de {progreso.total} analizados · {lastScan}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Leyenda del score */}
        {oportunidades.length > 0 && (
          <div className="flex items-center gap-4 mb-4 text-xs text-[#52525B]">
            <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-400" /><span className="text-yellow-400">90+</span> Excepcional</span>
            <span><span className="text-[#00FF88]">80-89</span> Muy fuerte</span>
            <span><span className="text-[#00D4FF]">70-79</span> Fuerte</span>
            <span className="ml-auto text-[#52525B]">Ordenados por score combinado (ADX 40% · MTF 30% · Volumen 20% · MACD 10%)</span>
          </div>
        )}

        {/* Resultados */}
        {oportunidades.map((op, i) => (
          <Card key={op.symbol} className={`bg-[#18181B] border-[#27272A] mb-3 ${i === 0 ? 'ring-1 ring-yellow-400/30' : ''}`}>
            <CardContent className="py-3 px-4">
              {/* Fila principal */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Score */}
                <ScoreBadge score={op.score} />

                {/* Par */}
                <div className="min-w-[70px]">
                  <span className="text-white font-bold">{op.symbol.replace('/USDT', '')}</span>
                  <span className="text-[#52525B] text-xs">/USDT</span>
                </div>

                {/* Dirección */}
                <span className={`flex items-center gap-1 font-bold text-sm min-w-[65px] ${op.direccion === 'ALZA' ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                  {op.direccion === 'ALZA' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {op.direccion}
                </span>

                {/* Multi-timeframe */}
                <div className="flex items-center gap-3 bg-[#09090B] rounded-lg px-3 py-1.5">
                  <TfBadge label="1h" dir={op.direccion} adx={op.adx_1h} />
                  <div className="w-px h-6 bg-[#27272A]" />
                  <TfBadge label="4h" dir={op.dir_4h} adx={op.adx_4h} />
                  <div className="w-px h-6 bg-[#27272A]" />
                  <TfBadge label="1d" dir={op.dir_1d} adx={op.adx_1d} />
                </div>

                {/* Alineación */}
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${op.timeframes_alineados === 3 ? 'bg-[#00FF88]/10 text-[#00FF88]' : op.timeframes_alineados === 2 ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#27272A] text-[#A1A1AA]'}`}>
                  {op.timeframes_alineados}/3 TF
                </span>

                {/* Etapa de la tendencia */}
                {op.etapa_tendencia && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                    op.etapa_tendencia === 'joven (punto ideal)' ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                    op.etapa_tendencia === 'agotada (riesgo de giro)' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                    op.etapa_tendencia === 'recién arrancando (sin confirmar)' ? 'bg-yellow-400/10 text-yellow-400' :
                    'bg-[#27272A] text-[#A1A1AA]'}`}
                    title={op.edad_tendencia != null ? `${op.edad_tendencia} velas con ADX≥25` : ''}>
                    {op.etapa_tendencia}
                  </span>
                )}

                {/* Métricas */}
                <div className="flex gap-3 text-xs font-mono">
                  <span className="text-[#A1A1AA]">RSI <span className={op.rsi_1h > 70 ? 'text-[#FF3B30]' : op.rsi_1h < 30 ? 'text-[#00FF88]' : 'text-white'}>{op.rsi_1h}</span></span>
                  <span className="text-[#A1A1AA]">Vol <span className={op.vol_ratio_1h >= 1.5 ? 'text-[#00FF88]' : 'text-white'}>×{op.vol_ratio_1h}</span></span>
                  <span className={op.cambio_24h >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}>
                    24h: {op.cambio_24h >= 0 ? '+' : ''}{op.cambio_24h}%
                  </span>
                </div>

                {/* Precio y advertencia */}
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono text-sm">${op.precio < 1 ? op.precio.toFixed(8) : op.precio.toFixed(2)}</span>
                  {op.cerca_nivel && <AlertTriangle className="w-4 h-4 text-yellow-400" title="Cerca de soporte/resistencia" />}
                </div>

                <button onClick={() => abrirModal(op)}
                  className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20 hover:bg-[#00D4FF]/20 transition-all">
                  Operar
                </button>
              </div>

              {/* Recomendación de Atenea */}
              <p className={`text-xs mt-2 ${op.recomendacion.startsWith('OPORTUNIDAD') ? 'text-[#00FF88] font-medium' : 'text-[#A1A1AA]'} italic`}>
                Atenea: {op.recomendacion}
              </p>

              {/* Price Action */}
              {op.pa_evento && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[10px] text-[#52525B]">
                  <span className="font-semibold text-[#A1A1AA]">PA:</span>
                  <span className={op.pa_estructura === 'HH_HL' ? 'text-[#00FF88]' : op.pa_estructura === 'LH_LL' ? 'text-[#FF3B30]' : ''}>
                    {op.pa_estructura === 'HH_HL' ? '📈 HH/HL alcista' : op.pa_estructura === 'LH_LL' ? '📉 LH/LL bajista' : 'Sin estructura'}
                  </span>
                  <span className="text-[#27272A]">·</span>
                  <span className={op.pa_emas === 'alcista' ? 'text-[#00FF88]' : op.pa_emas === 'bajista' ? 'text-[#FF3B30]' : ''}>
                    EMA {op.pa_emas === 'alcista' ? '↑ alcista' : op.pa_emas === 'bajista' ? '↓ bajista' : '~ mixta'}
                  </span>
                  <span className="text-[#27272A]">·</span>
                  <span className={op.pa_evento === 'ruptura_real' ? 'text-[#00FF88] font-bold' : op.pa_evento === 'falsa_ruptura' ? 'text-yellow-400' : op.pa_evento === 'rebote' ? 'text-[#00D4FF]' : ''}>
                    {op.pa_evento === 'ruptura_real' ? '✅ Ruptura real' : op.pa_evento === 'falsa_ruptura' ? '⚠️ Falsa ruptura' : op.pa_evento === 'rebote' ? '↩ Rebote' : op.pa_evento === 'tendencia_fuerte' ? '💪 Tendencia fuerte' : 'lateral'}
                  </span>
                  {op.pa_soporte && (
                    <>
                      <span className="text-[#27272A]">·</span>
                      <span>S ${op.pa_soporte.precio < 1 ? op.pa_soporte.precio.toFixed(8) : op.pa_soporte.precio.toFixed(2)} <span className="text-[#FF3B30]">({op.pa_soporte.pct_distancia}%↓</span> {op.pa_soporte.toques}t)</span>
                    </>
                  )}
                  {op.pa_resistencia && (
                    <>
                      <span className="text-[#27272A]">·</span>
                      <span>R ${op.pa_resistencia.precio < 1 ? op.pa_resistencia.precio.toFixed(8) : op.pa_resistencia.precio.toFixed(2)} <span className="text-[#00FF88]">({op.pa_resistencia.pct_distancia}%↑</span> {op.pa_resistencia.toques}t)</span>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {!loading && oportunidades.length === 0 && lastScan && (
          <div className="text-center py-12 text-[#52525B]">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No se encontraron tendencias con score ≥ 70 en este momento</p>
            <p className="text-xs mt-1">Los mercados pueden estar laterales. Prueba más tarde o con otro exchange.</p>
          </div>
        )}
      </div>

      {/* Modal: Operar manual o con bots */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={cerrarModal}>
          <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-bold text-lg">{modal.symbol}</h2>
                <span className={`text-sm font-bold ${modal.direccion === 'ALZA' ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                  {modal.direccion === 'ALZA' ? '▲' : '▼'} {modal.direccion} · Score {modal.score}
                </span>
              </div>
              <button onClick={cerrarModal} className="text-[#52525B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <p className="text-[#A1A1AA] text-xs mb-5 italic">{modal.recomendacion}</p>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {/* Opción Manual */}
              <button onClick={() => irManual(modal.symbol)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#09090B] border border-[#27272A] hover:border-[#00D4FF] hover:bg-[#00D4FF]/5 transition-all group">
                <div className="w-10 h-10 rounded-full bg-[#00D4FF]/10 flex items-center justify-center group-hover:bg-[#00D4FF]/20">
                  <ChartLine className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <span className="text-white font-bold text-sm">Manual</span>
                <span className="text-[#52525B] text-xs text-center">Abrir gráfico y operar tú mismo</span>
              </button>

              {/* Opción Bots */}
              <button onClick={() => irBots(modal.symbol)}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#09090B] border border-[#27272A] hover:border-[#00FF88] hover:bg-[#00FF88]/5 transition-all group">
                <div className="w-10 h-10 rounded-full bg-[#00FF88]/10 flex items-center justify-center group-hover:bg-[#00FF88]/20">
                  <Bot className="w-5 h-5 text-[#00FF88]" />
                </div>
                <span className="text-white font-bold text-sm">Con Bots</span>
                <span className="text-[#52525B] text-xs text-center">Atenea activa el bot adecuado</span>
              </button>
            </div>

            {/* Recomendación de bots según dirección */}
            {PLAYBOOK_BOTS[modal.direccion] && (
              <div className="bg-[#09090B] rounded-lg p-3 border border-[#27272A]">
                <p className="text-xs text-[#A1A1AA] mb-2 font-medium">Atenea recomienda para {modal.direccion}:</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {PLAYBOOK_BOTS[modal.direccion].estrategias.map(id => (
                    <span key={id} className="text-[10px] px-2 py-0.5 rounded bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20">
                      {NOMBRES_ESTRATEGIAS[id] || id}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-[#52525B]">Evitar: {PLAYBOOK_BOTS[modal.direccion].evitar.map(id => NOMBRES_ESTRATEGIAS[id] || id).join(', ')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Scanner;
